<template>
  <PaginatedList
    v-bind="$attrs"
    :columns="[
      {
        name: 'ReferenceNumber',
        title: resourceHelper.getString('RequestReferenceAndCounts'),
        dataTestAttribute: 'request-list-item-reference-number-and-count'
      },
      {
        name: 'Status',
        title: resourceHelper.getString('StatusAndPriority'),
        dataTestAttribute: 'request-list-item-status-and-priority'
      },
      {
        name: 'AssignedTo',
        title: resourceHelper.getString('AssignedToPersonaOrGroup'),
        cssClass: 'text-wrap-break-word',
        dataTestAttribute: 'request-list-assigned-to'
      },
      {
        name: 'CreatedBy',
        title: resourceHelper.getString('CreatedBy'),
        cssClass: 'text-wrap-break-word',
        dataTestAttribute: 'request-list-item-created-by'
      },
      {
        name: 'RequiredBy',
        title: resourceHelper.getString('RequiredByOrRespondedOn'),
        cssClass: 'text-wrap-break-word',
        dataTestAttribute: 'request-list-item-required-by-or-responded-on' },
      {
        name: 'SentTo',
        title: resourceHelper.getString('SentToRecipientList'),
        cssClass: 'text-wrap-break-word',
        dataTestAttribute: 'request-list-item-sent-to'
      }]"
    :items="requests"
    :highlighted-index="highlightedIndex"
    :actions="[
      {
        title: resourceHelper.getString('ViewRequest'),
        icon: 'fa-regular fa-eye',
        eventName: RequestListActionTypes.viewRequest,
        dataTestAttribute: 'request-list-action-view-request'
      },
      {
        title: resourceHelper.getString('CreateReport'),
        icon: 'fa-regular fa-file request-list__ellipsis-dropdown-icon',
        eventName: RequestListActionTypes.createReport,
        dataTestAttribute: 'request-list-action-create-report'
      }]"
    :hide-loading-spinner="true"
    @action-clicked="onActionClicked"
  >
    <template #ReferenceNumber="opts">
      <div class="request-list__reference-container">
        <PaginatedListLinkField
          data-test="request-list-item-reference-number-link"
          :text="opts.item.referenceNumber"
          :href="'/request/' + opts.item.requestId"
        />
        <div
          v-if="opts.item.discussionMessagesCount"
          class="request-list__counts-container mt-3"
          data-test="request-list-item-discussion-messages-count"
        >
          <i
            v-tooltip.top="{
              value: $localize('DiscussionMessageCountTooltip'),
              showDelay: 600
            }"
            class="fa-solid fa-comments request-list__counts-icon"
            aria-hidden="true"
          />
          <span class="visually-hidden">
            {{ $localize('Messages') }}
          </span>
          <span>
            {{ opts.item.discussionMessagesCount }}
          </span>
        </div>
      </div>
    </template>

    <template #Status="opts">
      <PaginatedListLabeledField
        :label="$localize('Status')"
        data-test-attribute="request-list-item-status"
      >
        {{ opts.item.requestStatusForDisplay }}
      </PaginatedListLabeledField>
      <PaginatedListLabeledField
        v-if="opts.item.requestPriorityForDisplay"
        :label="$localize('Priority')"
        data-test-attribute="request-list-item-priority"
      >
        {{ opts.item.requestPriorityForDisplay }}
      </PaginatedListLabeledField>
    </template>

    <template #AssignedTo="opts">
      <PaginatedListLabeledField
        :label="$localize('AssignedToPersonaOrGroup')"
        data-test-attribute="request-list-item-assigned-to"
      >
        <div
          v-if="opts.item.assignedPersonaName"
          data-test="request-list-item-assigned-to-persona"
        >
          <i
            class="fa-solid fa-user request-list__inline-text-icon"
            :title="$localize('User')"
          />
          <span>{{ opts.item.assignedPersonaName }}</span>
        </div>
        <div
          v-if="opts.item.assignedGroupName"
          data-test="request-list-item-assigned-to-group"
        >
          <i
            class="fa-solid fa-users request-list__inline-text-icon"
            :title="$localize('Group')"
          />
          <span>{{ opts.item.assignedGroupName }}</span>
        </div>
        <div
          v-if="!opts.item.assignedGroupName && !opts.item.assignedPersonaName"
          class="request-list__assigned-to--empty"
          data-test="request-list-item-assigned-to-unassigned"
        >
          {{ $localize('Unassigned') }}
        </div>
      </PaginatedListLabeledField>
    </template>

    <template #CreatedBy="opts">
      <PaginatedListLabeledField
        :label="$localize('CreatedBy')"
        data-test-attribute="request-list-item-created-by"
      >
        <div>{{ opts.item.investigatorDisplayName }}</div>
        <div class="request-list__secondary-value" data-test="request-list-item-agency">
          {{ opts.item.agencyName }}
        </div>
        <div class="request-list__secondary-value" data-test="request-list-item-datetime">
          <span>
            {{ moment(opts.item.requestDateTime).format('LLL') }}
          </span>
        </div>
      </PaginatedListLabeledField>
    </template>

    <template #RequiredBy="opts">
      <PaginatedListLabeledField
        v-if="opts.item.responseTimestamp"
        :label="$localize('RespondedOn')"
        data-test-attribute="request-list-item-responded-on"
      >
        <div>
          {{ moment(opts.item.responseTimestamp).format('LL') }}
        </div>
        <div
          class="request-list__secondary-value"
          data-test="request-list-item-response-timestamp"
        >
          {{ moment(opts.item.responseTimestamp).format('LT') }}
        </div>
      </PaginatedListLabeledField>
      <PaginatedListLabeledField
        v-else
        :label="$localize('RequiredBy')"
        data-test-attribute="request-list-item-required-by"
      >
        <div>
          {{ moment(opts.item.requiredByDate).format('LL') }}
        </div>
        <RequiredBySummary
          v-if="!opts.item.hasResponse && opts.item.requiredByDate"
          :required-by="convertStringToDate(opts.item.requiredByDate)"
          data-test="request-list-item-required-by-summary"
        />
      </PaginatedListLabeledField>
      <div
        v-if="opts.item.caseNameClaim && opts.item.caseNameClaim.displayName"
        class="heading mt-3"
        data-test="request-list-item-case-name"
      >
        {{ opts.item.caseNameClaim.displayName }}
      </div>
      <div
        v-else-if="opts.item.caseNameClaim && !opts.item.caseNameClaim.displayName"
        class="heading mt-3"
        data-test="request-list-item-case-name"
      >
        {{ opts.item.caseNameClaim.key }}
      </div>
      <div
        v-if="opts.item.caseNameClaim"
        data-test="request-list-item-case-name"
      >
        {{ opts.item.caseNameClaim.value }}
      </div>
    </template>

    <template #SentTo="opts">
      <PaginatedListLabeledField
        :label="$localize('SentToRecipientList')"
        data-test-attribute="request-list-item-sent-to"
      >
        <CollapsedList
          :max-collapsed-items-count="3"
          :options="opts.item.sentToRecipientList"
          :no-items-string="resourceHelper.getString('CollapsibleListNoRecipients')"
        />
      </PaginatedListLabeledField>
      <div
        v-if="opts.item.rmsIdClaim && opts.item.rmsIdClaim.displayName"
        class="heading mt-3"
        data-test="request-list-item-rms-id"
      >
        {{ opts.item.rmsIdClaim.displayName }}
      </div>
      <div
        v-else-if="opts.item.rmsIdClaim && !opts.item.rmsIdClaim.displayName"
        class="heading mt-3"
        data-test="request-list-item-rms-id"
      >
        {{ opts.item.rmsIdClaim.key }}
      </div>
      <div
        v-if="opts.item.rmsIdClaim"
        data-test="request-list-item-rms-id"
      >
        {{ opts.item.rmsIdClaim.value }}
      </div>
    </template>
  </PaginatedList>

  <CreateReportModal
    v-model="report.showModal"
    :is-danger="false"
    :report-item-reference-number="report.itemReferenceNumber"
    :report-item-id="report.itemId"
    :item-type="CreateReportModalTypes.request"
  />
</template>
<script lang="ts" setup>
  import PaginatedList from '@/VueComponents/SharedComponents/PaginatedList/PaginatedList.vue';
  import resourceHelper from '@/Utils/resourceHelper';
  import PaginatedListLinkField from '@/VueComponents/SharedComponents/PaginatedList/PaginatedListLinkField.vue';
  import PaginatedListLabeledField from '@/VueComponents/SharedComponents/PaginatedList/PaginatedListLabeledField.vue';
  import { computed, reactive } from 'vue';
  import moment from 'moment';
  import CollapsedList from '@/VueComponents/SharedComponents/CollapsedList/CollapsedList.vue';
  import CreateReportModal from '@/VueComponents/Modals/CreateReportModal.vue';
  import { CreateReportModalTypes } from '@/Types/Enums/createReportModalTypes';
  import RequiredBySummary from '@/VueComponents/Requests/RequestList/RequiredBySummary/RequiredBySummary.vue';
  import { RequestListActionTypes } from '@/VueComponents/Requests/RequestList/Enums/requestListActionTypes';
  import { useRouter } from 'vue-router';

  const router = useRouter();

  const props = defineProps<{
    requests: any[],
    lastAccessedRequestId?: string
  }>();

  const report = reactive({
    showModal: false,
    itemReferenceNumber: '',
    itemId: ''
  });

  const highlightedIndex = computed(() => {
    for (let i = 0; i < props.requests.length; i++) {
      if (props.requests[i].requestId === props.lastAccessedRequestId) {
        return i;
      }
    }

    return -1;
  });

  function onActionClicked(actionName: string, payload: any) {
    switch (actionName) {
    case RequestListActionTypes.viewRequest:
      viewRequestClicked(payload);
      break;
    case RequestListActionTypes.createReport:
      createReportClicked(payload);
      break;
    }
  }

  function viewRequestClicked(payload) {
    router.push('/request/' + payload.item.requestId);
  }

  function createReportClicked(payload) {
    report.itemReferenceNumber = payload.item.referenceNumber;
    report.itemId = payload.item.requestId;
    report.showModal = true;
  }

  function convertStringToDate(str) {
    // convert string in ISO_8601 format YYYY-MM-DDTHH:mm:ss
    const date = moment(str, moment.ISO_8601, true).toDate();
    return date;
  }
</script>